/**
 * Курс, для которого лекарства смешивают.
 *
 * Две мази в одном нанесении, две капли подряд — для человека это одно
 * применение, а для аптечки два тюбика. Раньше запись была одна на всё, и из
 * этого следовало неверное: переименование тюбика в аптечке меняло название
 * курса, а срок годности и остаток были общими на две разные коробки.
 *
 * Поэтому курс-смесь — это применение, а не упаковка. В аптечке лежат его
 * составляющие, у каждой свой срок и свой остаток, а курс ссылается на них.
 */

import type { Medication } from '@/shared/db/schema';

/** Состав курса. Пусто — обычное лекарство. */
function componentIdsOf(medication: Pick<Medication, 'componentIds'>): string[] {
  /*
    Поле может отсутствовать: запись пришла из копии, снятой прежней версией,
    или с устройства, которое ещё не обновилось. Падать на этом нельзя — на
    чтении состава держится и экран здоровья, и планировщик напоминаний, и
    одна такая запись погасила бы их целиком.
  */
  return medication.componentIds ?? [];
}

/** Курс состоит из нескольких лекарств. */
export function isMix(medication: Pick<Medication, 'componentIds'>): boolean {
  return componentIdsOf(medication).length > 0;
}

/**
 * Карточки составляющих в том порядке, в котором их выбрали.
 *
 * Пропавшие ссылки молча пропускаются: составляющую могли удалить, и смесь от
 * этого не должна исчезать с экрана — человек увидит, что осталось, и
 * поправит состав.
 */
export function mixComponents(
  medication: Pick<Medication, 'componentIds'>,
  all: readonly Medication[],
): Medication[] {
  const byId = new Map(all.map((item) => [item.id, item]));

  return componentIdsOf(medication)
    .map((id) => byId.get(id))
    .filter((item): item is Medication => item !== undefined && item.deletedAt === null);
}

/** «Акридерм + Клотримазол» — из чего смешивать. */
export function describeMix(components: readonly Medication[]): string {
  return components.map((component) => component.name).join(' + ');
}
